<template>
    <div>
        <page-header
            :text="$t('navigation.users_admin')"
            :searchField="{
                placeholder: $t('users.search'),
                inputEmitEvent: 'search',
                allowedFields: searchFields,
                defaultField: 'name',
                savedSearchName: 'search-user-overview',
            }"
            @search="searchHandler"
        />

        <div class="table-wrapper">
            <table class="table table-filter table--striped table--filterable">
                <thead>
                    <tr>
                        <th
                            v-for="field in fields"
                            :key="field.value"
                            :class="{ 'table-filter__trigger': field.value !== 'status' }"
                            :width="field.width"
                        >
                            <div>{{ $t(field.title) }}</div>
                            <div
                                v-if="field.sortable"
                                class="table-filter__sorting"
                                @click="sortClickHandler(field.value)"
                                :class="{
                                    asc: activeSorting[field.value] === 'asc',
                                    desc: activeSorting[field.value] === 'desc',
                                }"
                            >
                                <i class="icon icon--common-caret"></i>
                            </div>
                        </th>
                        <th width="8%" v-if="$can('edit', 'User')">
                            {{ $t('users_admin.access') }}
                        </th>
                        <th width="8%" v-if="$can('delete', 'User')">
                            {{ $t('buttons.delete') }}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="user in filteredUsers" :key="user.id">
                        <td class="status__wrap">
                            <span
                                :title="$t('users.unconfirmed')"
                                v-if="user.status === 'unconfirmed'"
                                class="status"
                                :class="'status--' + user.status"
                                >&nbsp;</span
                            >
                            <span
                                :title="$t('users.' + user.status)"
                                v-else
                                @click="changeUser(user.status, user.id)"
                                class="status status--clickable"
                                :class="'status--' + user.status"
                                >&nbsp;</span
                            >
                        </td>
                        <td>
                            <router-link
                                :to="{
                                    name: 'user-management',
                                    params: { id: user.id },
                                }"
                            >
                                <b>{{ user.name }}</b>
                            </router-link>
                        </td>
                        <td>{{ user.email }}</td>
                        <td class="uppercase">{{ user.country }}</td>
                        <td>{{ user.last_login }}</td>
                        <td class="capitalize">
                            {{ $t(`common.${user.sso_user ? 'yes' : 'no'}`) }}
                        </td>
                        <td>
                            <router-link
                                v-if="user.organization"
                                :to="{
                                    name: 'organization-management',
                                    params: { id: user.organization.id },
                                }"
                                class="small-link"
                            >
                                {{ user.organization.name }}
                            </router-link>
                        </td>
                        <td v-if="$can('edit', 'User')">
                            <a
                                class="button button--attention"
                                v-show="!user.loading && user.organization"
                                @click="editUserPermissions(user)"
                            >
                                <i class="icon icon--action-edit-in-circle"></i>
                                <span>{{ $t('users_admin.edit') }}</span>
                            </a>
                            <i
                                class="icon icon--common-spinner spinner"
                                :class="{ 'spinner--finished': !user.loading }"
                            ></i>
                        </td>
                        <td v-if="$can('delete', 'User')">
                            <a
                                class="button button--attention"
                                @click="removeUser(user)"
                                v-show="!user.loading"
                            >
                                <i class="icon icon--action-decline"></i>
                                <span>{{ $t('users_admin.remove') }}</span>
                            </a>
                        </td>
                    </tr>
                </tbody>

                <TablePagination
                    v-if="paginationData"
                    :paginationData="paginationData"
                    :visibleAmount="searchedItems ? searchedItems.length : items.length"
                    @goToPrevPage="prevPageHandler"
                    @goToNextPage="nextPageHandler"
                />
            </table>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import TablePagination from '@/components/TablePagination';
import * as moment from 'moment';
import paginatedTable from '@/mixins/paginatedTable';

export default {
    components: {
        PageHeader,
        TablePagination,
    },
    mixins: [paginatedTable],
    data() {
        return {
            apiType: 'users',
            responseField: 'users',
            searchApi: 'search',
            searchFields: ['name', 'email', 'country'],
            deletePromises: [],
            fields: [
                {
                    value: 'status',
                    title: 'users_admin.status',
                    sortable: false,
                    width: '3%',
                },
                {
                    value: 'name',
                    title: 'users_admin.name',
                    sortable: true,
                    width: '27%',
                },
                {
                    value: 'email',
                    title: 'users_admin.email',
                    sortable: true,
                    width: '27%',
                },
                {
                    value: 'country',
                    title: 'users_admin.country',
                    sortable: true,
                    width: '7%',
                },
                {
                    value: 'last_sign_in_at',
                    title: 'users_admin.last_login',
                    sortable: true,
                    width: '14%',
                },
                {
                    value: 'sso_user',
                    title: 'users_admin.sso',
                    sortable: false,
                    width: '6%',
                },
                {
                    value: 'organization.name',
                    title: 'fields.organization',
                    sortable: false,
                    width: '12%',
                },
            ],
        };
    },
    created() {
        this.loadItems();
    },
    computed: {
        filteredUsers() {
            if (!this.items) return [];

            let filtered = this.searchedItems ? [...this.searchedItems] : [...this.items];

            filtered.forEach(f => {
                if (f.disabled) {
                    f.status = 'inactive';
                } else if (f.email_confirmed === false) {
                    f.status = 'unconfirmed';
                } else {
                    f.status = 'active';
                }
            });

            return this.prepareUsers(filtered);
        },
    },
    methods: {
        changeUser(status, userid) {
            const activate = status === 'active' ? false : true;
            this.$modal.show(
                () => import('@/views/modal/AdjustUser.vue'),
                {
                    activate: activate,
                    userid: userid,
                    successCallback: (d, id) => {
                        this.updateUserStatus(d, id);
                    },
                },
                {
                    name: 'AdjustUser',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },

        async updateUserStatus(status, id) {
            const that = this;
            try {
                const response = await api.users.updateUserStatus(id, status);

                if (this.searchedItems !== false) {
                    this.searchedItems.forEach(function(i, key) {
                        if (i.id === id) {
                            that.$set(that.searchedItems[key], 'disabled', response.data.disabled);
                        }
                    });
                } else {
                    this.items.forEach(function(i, key) {
                        if (i.id === id) {
                            that.$set(that.items[key], 'disabled', response.data.disabled);
                        }
                    });
                }
            } catch (error) {
                console.error(error);
                this.loading = false;
                this.$notify({
                    type: 'error',
                    text: this.$t('errors.error_occurred'),
                });
            }
        },

        prepareUsers(users) {
            return users.map(user => {
                let preparedUser = Object.assign({}, user);
                preparedUser.loading = false;
                preparedUser.sso_user = !!preparedUser.sso_id;
                preparedUser.last_login = moment(preparedUser.last_sign_in_at).isValid()
                    ? moment(preparedUser.last_sign_in_at).format('YYYY-MM-DD HH:mm:ss')
                    : '-';
                return preparedUser;
            });
        },

        editUserPermissions(user) {
            this.$modal.show(
                () => import('@/views/modal/EditUserPermissions.vue'),
                {
                    user: user,
                },
                {
                    name: 'UserPermissions',
                    scrollable: true,
                    width: 600,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                },
                {
                    'before-close': () => {
                        this.loadUsers();
                    },
                }
            );
        },

        removeUser(user) {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('users_admin.delete_user_text'),
                    smallText: `
                        ${this.$t('users_admin.name')}: ${user.name} <br>
                        ${this.$t('users_admin.email')}: ${user.email} <br>
                        ID: ${user.id}
                    `,
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: this.$t('buttons.delete'),
                    successCallback: () => {
                        user.loading = true;

                        this.removeUserCallback(user);
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        async removeUserCallback(user) {
            const userSystems = await this.getUserSystems(user.id);
            if (userSystems && userSystems.length > 0) {
                this.removeUserSystems(userSystems, user);
            } else {
                const deleteUserPromise = this.deleteUser(user);
                this.deletePromises.push(deleteUserPromise);
                this.resolveDeletePromises();
            }
        },
        async removeUserSystems(systems, user) {
            const text = `
                ${this.$t('users_admin.delete_owned_systems_text')}
                <br>
                ${systems.map(system => `${system.name}<br>`).join('')}
            `;

            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: text,
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: this.$t('buttons.delete'),
                    successCallback: async () => {
                        systems.forEach(system => {
                            this.deletePromises.push(this.deleteSystem(system.id));
                        });
                        const deleteUserPromise = this.deleteUser(user);
                        this.deletePromises.push(deleteUserPromise);
                        this.resolveDeletePromises();
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        async getUserSystems(userid) {
            try {
                const response = await api.users.userSystems(userid, { disable_pagination: true });
                if (response) {
                    return response.data.systems;
                }
                return;
            } catch (error) {
                console.log('error getting user system');
                console.error(error);
            }
        },
        deleteUser(user) {
            return new Promise((resolve, reject) => {
                api.users
                    .delete({ userid: user.id })
                    .then(() => {
                        resolve();
                    })
                    .catch(() => {
                        this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
                        reject();
                    });
            });
        },
        deleteSystem(systemid) {
            return new Promise((resolve, reject) => {
                api.systems
                    .delete({ id: systemid })
                    .then(() => {
                        resolve();
                    })
                    .catch(() => {
                        console.log('error deleting system');
                        this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
                        reject();
                    });
            });
        },
        async resolveDeletePromises() {
            if (this.deletePromises.length > 0) {
                await Promise.all(this.deletePromises);
                this.deletePromises = [];
            }

            if (this.activeSearchTerm) {
                this.searchItems(this.activeSearchTerm);
            } else {
                this.loadItems();
            }
        },
    },
};
</script>

<style lang="scss">
.status {
    display: inline-block;
    width: 24px;
    height: 14px;
    border-radius: 7px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: -5px;
    background: $color-gray-dark;

    &:after {
        content: '';
        width: 10px;
        height: 10px;
        border-radius: 50%;
        position: absolute;
        left: 2px;
        top: 2px;
        transition: all 0.25s ease;
    }

    &--active {
        &:after {
            background: $color-success;
        }
    }
    &--inactive {
        &:after {
            background: $color-danger;
            left: 12px;
        }
    }
    &--unconfirmed {
        &:after {
            background: $color-warning;
            left: 7px;
        }
    }

    &--clickable {
        cursor: pointer;
    }

    &__wrap {
        position: relative;
    }
}
</style>
